import {
  SettingsPermissions,
  SupportPermissions,
} from "@redotech/account-common/permissions";
import { MongoUserId } from "@redotech/effect-api-shared/ids/mongo-ids";
import {
  DefaultView,
  isDefaultView,
} from "@redotech/effect-api-shared/models/support-ticket/default-view";
import type { FolderResponse } from "@redotech/effect-api-shared/rpc/view-folder";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import EditPencilIcon from "@redotech/redo-icons/edit-01.svg";
import FolderOpenIcon from "@redotech/redo-icons/folder-open.svg";
import FolderIcon from "@redotech/redo-icons/folder.svg";
import QuestionCircleIcon from "@redotech/redo-icons/help-circle.svg";
import SUPPORT_ICON from "@redotech/redo-icons/message-text-square-02.svg";
import SUPPORT_ICON_FILLED from "@redotech/redo-icons/message-text-square-02_filled.svg";
import TicketIcon from "@redotech/redo-icons/ticket-02.svg";
import TicketFilledIcon from "@redotech/redo-icons/ticket-02_filled.svg";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { useCapability } from "@redotech/redo-merchant-app-common/use-capability";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { userNameCache } from "@redotech/redo-merchant-support-app/v2/filters/assignees-filter";
import {
  useDefaultViewDescription,
  useDefaultViewDisplayName,
} from "@redotech/redo-merchant-support-app/v2/hooks/use-default-view-copy";
import { useSupportTicketCounts } from "@redotech/redo-merchant-support-app/v2/hooks/use-support-ticket-counts";
import { useSupportV2FolderActions } from "@redotech/redo-merchant-support-app/v2/hooks/use-support-v2-folder-actions";
import { useSupportV2UserOrdering } from "@redotech/redo-merchant-support-app/v2/hooks/use-support-v2-user-ordering";
import { useSupportV2ViewsAndFolders } from "@redotech/redo-merchant-support-app/v2/hooks/use-support-v2-views-and-folders";
import { SupportV2FolderModal } from "@redotech/redo-merchant-support-app/v2/views/folders/support-v2-folder-modal";
import { SupportTicketViewHoverContent } from "@redotech/redo-merchant-support-app/v2/views/support-ticket-view-hover-content";
import {
  AI_CONVERSATIONS_VIEW_FILTER_GROUP,
  ALL_VIEW_FILTER_GROUP,
  ARCHIVED_VIEW_FILTER_GROUP,
  buildAssignedViewFilterGroup,
  buildMentionsViewFilterGroup,
  defaultViewScope,
  DRAFTS_VIEW_FILTER_GROUP,
  OPEN_VIEW_FILTER_GROUP,
  SENT_VIEW_FILTER_GROUP,
  SPAM_VIEW_FILTER_GROUP,
  URL_VIEW_SCOPE_PARAM,
  withCustomViewDefaults,
  withInitialViewDefaults,
} from "@redotech/redo-support-v2/query/default-views";
import { orderSupportNavEntries } from "@redotech/redo-support-v2/query/support-ticket-nav-ordering";
import type { FilterGroup } from "@redotech/redo-views-common/advanced-filters/advanced-filter-data";
import { encodeAdvancedFilterGroup } from "@redotech/redo-web/advanced-filters/advanced-filter";
import { LinkPrimaryTab } from "@redotech/redo-web/arbiter-components/navigation/primary/redo-primary-nav";
import { PageNavigationEntry } from "@redotech/redo-web/arbiter-components/navigation/redo-side-navigation";
import {
  RedoNavDraggableList,
  RedoNavDraggableListItem,
} from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-nav-draggable-list";
import { SecondaryNavigationData } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav";
import {
  RedoSecondaryNavItem,
  useNavTreeNode,
} from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import { memo, type ReactNode, useMemo, useState } from "react";
import { useLocation, useMatch } from "react-router-dom";
import { SupportAnalyticsItems } from "./analytics";
import { ConciergeItems } from "./concierge";
import { CsatItems } from "./csat";
import { SupportDashboardItems } from "./dashboard";
import { DevItems } from "./dev";
import { useHasAnyOpenConversations } from "./use-has-any-open-conversations";

export function useSupportNavigation(): PageNavigationEntry | null {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  const supportEnabled = useCapability("support");

  const canViewSupport = useHasPermission(SupportPermissions.VIEW_SUPPORT);

  const hasAnyNonZeroCounts = useHasAnyOpenConversations();

  if (!canViewSupport) {
    return null;
  }

  const baseUrl = routes.supportBaseUrl;
  const primaryUrl = routes.getSupportHref({ enabled: supportEnabled });

  const primaryTab: LinkPrimaryTab = {
    id: "support",
    hasNotification: hasAnyNonZeroCounts,
    UnfilledIcon: SUPPORT_ICON,
    FilledIcon: SUPPORT_ICON_FILLED,
    type: "link",
    pathRoot: baseUrl,
    href: primaryUrl,
    friendlyName: t("Support", {
      context: "primary navigation section for customer support work",
    }),
  };

  const secondaryNavigationData: SecondaryNavigationData = {
    id: "support",
    name: t("Support", {
      context: "primary navigation section for customer support work",
    }),
    NavContent: SecondarySupportSection,
  };

  return {
    primaryTab,
    secondaryNavigationData,
    isActiveProduct: supportEnabled,
  };
}

const SecondarySupportSection = memo(function SecondarySupportSection() {
  const { t } = useTranslation();
  const { id: nodeId, TreeContext } = useNavTreeNode({
    friendlyName: t("Support", {
      context: "primary navigation section for customer support work",
    }),
  });

  return (
    <TreeContext.Provider value={nodeId}>
      <SupportDashboardItems />
      <SupportAnalyticsItems />
      <ConciergeItems />
      <FaqItems />
      <CsatItems />
      <DevItems />
      <SupportV2TicketsItem />
    </TreeContext.Provider>
  );
});

const SupportV2TicketsItem = memo(function SupportV2TicketsItem() {
  return <SupportV2TicketsItemContent />;
});

function buildDefaultViewHref(
  teamId: string,
  defaultView: DefaultView,
  filterGroup: FilterGroup,
): string {
  const base = `/stores/${teamId}/support-v2/`;
  const params = new URLSearchParams();
  params.set("defaultView", defaultView);
  const scope = defaultViewScope(defaultView);
  if (scope) {
    params.set(URL_VIEW_SCOPE_PARAM, scope);
  }
  try {
    const encoded = encodeAdvancedFilterGroup(
      withInitialViewDefaults(defaultView, filterGroup),
    );
    params.set("fg", encoded);
  } catch {
    // ignore encoding errors
  }
  return `${base}?${params.toString()}`;
}

function buildViewHref(
  teamId: string,
  viewId: string,
  filterGroup: unknown,
): string {
  const base = `/stores/${teamId}/support-v2/`;
  const params = new URLSearchParams();
  params.set("view", viewId);
  const effectiveFilterGroup = withCustomViewDefaults(
    (filterGroup as FilterGroup | null) ?? null,
  );
  try {
    const encoded = encodeAdvancedFilterGroup(effectiveFilterGroup);
    params.set("fg", encoded);
  } catch {
    // ignore encoding errors
  }
  return `${base}?${params.toString()}`;
}

function viewItemTooltip(
  title: string,
  filterGroup: FilterGroup,
  description?: string,
) {
  return (
    <SupportTicketViewHoverContent
      description={description}
      filterGroup={filterGroup}
      title={title}
    />
  );
}

function getDefaultViewFilterGroup(
  view: DefaultView,
  assignedFilterGroup: FilterGroup | null,
  mentionsFilterGroup: FilterGroup,
): FilterGroup {
  switch (view) {
    case DefaultView.ALL:
      return ALL_VIEW_FILTER_GROUP;
    case DefaultView.OPEN:
      return OPEN_VIEW_FILTER_GROUP;
    case DefaultView.ASSIGNED:
      return assignedFilterGroup ?? OPEN_VIEW_FILTER_GROUP;
    case DefaultView.MENTIONS:
      return mentionsFilterGroup;
    case DefaultView.DRAFTS:
      return DRAFTS_VIEW_FILTER_GROUP;
    case DefaultView.AI_CONVERSATIONS:
      return AI_CONVERSATIONS_VIEW_FILTER_GROUP;
    case DefaultView.SENT:
      return SENT_VIEW_FILTER_GROUP;
    case DefaultView.SPAM:
      return SPAM_VIEW_FILTER_GROUP;
    case DefaultView.ARCHIVED:
      return ARCHIVED_VIEW_FILTER_GROUP;
  }
}

// Default views shown above custom views / folders before any user reorder.
const TOP_DEFAULT_VIEWS: DefaultView[] = [
  DefaultView.ALL,
  DefaultView.OPEN,
  DefaultView.ASSIGNED,
  DefaultView.MENTIONS,
  DefaultView.DRAFTS,
  DefaultView.AI_CONVERSATIONS,
];
// Default views shown below custom views / folders before any user reorder.
const BOTTOM_DEFAULT_VIEWS: DefaultView[] = [
  DefaultView.SENT,
  DefaultView.SPAM,
  DefaultView.ARCHIVED,
];
// Default views that surface an unread count at the top level.
const DEFAULT_VIEWS_WITH_COUNT: ReadonlySet<DefaultView> = new Set([
  DefaultView.OPEN,
  DefaultView.ASSIGNED,
  DefaultView.MENTIONS,
  DefaultView.DRAFTS,
]);

// A reorderable top-level nav entry, persisted as `{ id, type }` ordering.
type SupportNavEntry =
  | { kind: "defaultView"; id: DefaultView }
  | { kind: "view"; id: string }
  | { kind: "folder"; id: string };

const SupportV2TicketsItemContent = memo(
  function SupportV2TicketsItemContent() {
    const { t } = useTranslation();
    const defaultViewDescription = useDefaultViewDescription();
    const defaultViewDisplayName = useDefaultViewDisplayName();
    const team = useRequiredContext(TeamContext);
    const location = useLocation();
    const user = useRequiredContext(UserContext);
    const { views, folders } = useSupportV2ViewsAndFolders();
    const { createFolder, updateFolder, deleteFolder } =
      useSupportV2FolderActions();
    const countsMap = useSupportTicketCounts(views);
    const { ordering, saveOrdering } = useSupportV2UserOrdering();

    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [folderBeingEdited, setFolderBeingEdited] =
      useState<FolderResponse | null>(null);

    const reservedFolderNames = useMemo(
      () => new Set(folders.map((f) => f.name)),
      [folders],
    );

    const folderlessViews = useMemo(
      () => views.filter((v) => !v.folderId),
      [views],
    );

    const matchChildPath = !!useMatch(`/stores/${team._id}/support-v2/*`);
    const matchRootPath = !!useMatch(`/stores/${team._id}/support-v2`);
    const anyChildSelected = matchChildPath || matchRootPath;

    const currentSearchParams = new URLSearchParams(location.search);
    const currentViewId = currentSearchParams.get("view");
    const currentDefaultView = currentSearchParams.get("defaultView");

    const assignedFilterGroup = useMemo(() => {
      if (user.name) {
        userNameCache.set(MongoUserId.fromString(user._id), user.name);
      }
      return buildAssignedViewFilterGroup(user._id);
    }, [user?._id, user?.name]);

    const mentionsFilterGroup = useMemo(
      () => buildMentionsViewFilterGroup(user._id),
      [user?._id],
    );

    // Map from DefaultView id → the folder that contains it (for deduplication)
    const folderDefaultViewsMap = useMemo(() => {
      const map = new Map<string, FolderResponse>();
      for (const folder of folders) {
        for (const dv of folder.defaultViews) {
          map.set(dv, folder);
        }
      }
      return map;
    }, [folders]);

    // All views for the folder modal: default views + custom views
    const allViewsForModal = useMemo(
      () => [
        ...Object.values(DefaultView).map((dv) => ({
          id: dv,
          name: defaultViewDisplayName(dv),
        })),
        ...views.map((v) => ({ id: String(v.id), name: v.name })),
      ],
      [defaultViewDisplayName, views],
    );

    // Single reorderable list: folderless default views, folders, and
    // folderless custom views, sorted by the user's saved ordering.
    const navEntries: RedoNavDraggableListItem<SupportNavEntry>[] =
      useMemo(() => {
        const itemByKey = new Map<
          string,
          RedoNavDraggableListItem<SupportNavEntry>
        >();

        for (const dv of [...TOP_DEFAULT_VIEWS, ...BOTTOM_DEFAULT_VIEWS]) {
          if (folderDefaultViewsMap.has(dv)) {
            continue;
          }
          const defaultFilterGroup = getDefaultViewFilterGroup(
            dv,
            assignedFilterGroup,
            mentionsFilterGroup,
          );
          itemByKey.set(`defaultView:${dv}`, {
            id: dv,
            friendlyName: defaultViewDisplayName(dv),
            itemTooltip: viewItemTooltip(
              defaultViewDisplayName(dv),
              withInitialViewDefaults(dv, defaultFilterGroup),
              defaultViewDescription(dv),
            ),
            notificationCount: DEFAULT_VIEWS_WITH_COUNT.has(dv)
              ? (countsMap.get(dv) ?? 0)
              : undefined,
            selected: currentDefaultView === dv,
            action: {
              type: "link" as const,
              href: buildDefaultViewHref(team._id, dv, defaultFilterGroup),
            },
            itemValue: { kind: "defaultView", id: dv },
          });
        }

        for (const folder of folders) {
          const folderViews = views.filter(
            (v) => String(v.folderId) === String(folder.id),
          );
          const folderDefaultViews = folder.defaultViews.filter(isDefaultView);
          const anyChildSelected =
            folderViews.some((v) => currentViewId === String(v.id)) ||
            (currentDefaultView != null &&
              isDefaultView(currentDefaultView) &&
              folderDefaultViews.includes(currentDefaultView));
          const folderChildren: ReactNode[] = [
            ...folderDefaultViews.map((dv) => (
              <SupportV2DefaultViewItem
                defaultView={dv}
                filterGroup={withInitialViewDefaults(
                  dv,
                  getDefaultViewFilterGroup(
                    dv,
                    assignedFilterGroup,
                    mentionsFilterGroup,
                  ),
                )}
                href={buildDefaultViewHref(
                  team._id,
                  dv,
                  getDefaultViewFilterGroup(
                    dv,
                    assignedFilterGroup,
                    mentionsFilterGroup,
                  ),
                )}
                key={dv}
                notificationCount={countsMap.get(dv) ?? 0}
                selected={currentDefaultView === dv}
              />
            )),
            ...folderViews.map((view) => (
              <SupportV2ViewItem
                filterGroup={withCustomViewDefaults(
                  (view.filterGroup as FilterGroup | null) ?? null,
                )}
                href={buildViewHref(
                  team._id,
                  String(view.id),
                  view.filterGroup,
                )}
                id={String(view.id)}
                key={String(view.id)}
                name={view.name}
                notificationCount={countsMap.get(String(view.id)) ?? 0}
                selected={currentViewId === String(view.id)}
              />
            )),
            <SupportV2EditFolderItem
              key="edit-folder"
              onClick={() => setFolderBeingEdited(folder)}
            />,
          ];
          itemByKey.set(`folder:${String(folder.id)}`, {
            id: String(folder.id),
            friendlyName: folder.name,
            isFolder: true,
            UnfilledIcon: FolderIcon,
            FilledIcon: FolderOpenIcon,
            anyChildSelected,
            action: { type: "button" },
            children: folderChildren,
            itemValue: { kind: "folder", id: String(folder.id) },
          });
        }

        for (const view of folderlessViews) {
          itemByKey.set(`view:${String(view.id)}`, {
            id: String(view.id),
            friendlyName: view.name,
            itemTooltip: viewItemTooltip(
              view.name,
              withCustomViewDefaults(
                (view.filterGroup as FilterGroup | null) ?? null,
              ),
            ),
            notificationCount: countsMap.get(String(view.id)) ?? 0,
            selected: currentViewId === String(view.id),
            action: {
              type: "link" as const,
              href: buildViewHref(team._id, String(view.id), view.filterGroup),
            },
            itemValue: { kind: "view", id: String(view.id) },
          });
        }

        const orderedRefs = orderSupportNavEntries({
          ordering,
          topDefaultViewIds: TOP_DEFAULT_VIEWS,
          bottomDefaultViewIds: BOTTOM_DEFAULT_VIEWS,
          folderedDefaultViewIds: new Set(folderDefaultViewsMap.keys()),
          folders: folders.map((f) => ({ id: String(f.id) })),
          folderlessViews: folderlessViews.map((v) => ({
            id: String(v.id),
            position: v.position,
          })),
        });

        return orderedRefs.flatMap((ref) => {
          const item = itemByKey.get(`${ref.kind}:${ref.id}`);
          return item ? [item] : [];
        });
      }, [
        ordering,
        folders,
        views,
        folderlessViews,
        folderDefaultViewsMap,
        countsMap,
        team._id,
        currentViewId,
        currentDefaultView,
        assignedFilterGroup,
        mentionsFilterGroup,
        defaultViewDescription,
        defaultViewDisplayName,
      ]);

    const handleReorder = useHandler(
      (newOrder: RedoNavDraggableListItem<SupportNavEntry>[]) => {
        void saveOrdering(
          newOrder.map((item) => ({
            id: item.itemValue.id,
            type: item.itemValue.kind === "folder" ? "folder" : "view",
          })),
        );
      },
    );

    return (
      <>
        <RedoSecondaryNavItem
          action={{ type: "link", href: `/stores/${team._id}/support-v2/` }}
          anyChildSelected={anyChildSelected}
          FilledIcon={TicketFilledIcon}
          friendlyName={t("Tickets", {
            context: "customer support navigation label",
          })}
          id="tickets"
          UnfilledIcon={TicketIcon}
        >
          {/* Default views, folders, and custom views in one reorderable list */}
          <RedoNavDraggableList
            draggableItems={navEntries}
            setDraggableItems={handleReorder}
          />

          <SupportCreateFolderItem onClick={() => setIsCreateModalOpen(true)} />
        </RedoSecondaryNavItem>

        <SupportV2FolderModal
          currentName=""
          currentViewIds={[]}
          isOpen={isCreateModalOpen}
          mode="create"
          onClose={() => setIsCreateModalOpen(false)}
          onSubmit={async (name, selectedViewIds, isPrivate) => {
            const defaultViewIds = selectedViewIds.filter(isDefaultView);
            const customViewIds = selectedViewIds.filter(
              (id) => !isDefaultView(id),
            );
            await createFolder(name, customViewIds, isPrivate, defaultViewIds);
            setIsCreateModalOpen(false);
          }}
          reservedFolderNames={reservedFolderNames}
          views={allViewsForModal}
        />

        {folderBeingEdited && (
          <SupportV2FolderModal
            currentName={folderBeingEdited.name}
            currentViewIds={[
              ...folderBeingEdited.defaultViews.filter(isDefaultView),
              ...views
                .filter(
                  (v) =>
                    v.folderId != null &&
                    String(v.folderId) === String(folderBeingEdited.id),
                )
                .map((v) => String(v.id)),
            ]}
            isOpen
            mode="edit"
            onClose={() => setFolderBeingEdited(null)}
            onDelete={async () => {
              await deleteFolder(String(folderBeingEdited.id));
              setFolderBeingEdited(null);
            }}
            onSubmit={async (name, selectedViewIds) => {
              const defaultViewIds = selectedViewIds.filter(isDefaultView);
              const customViewIds = selectedViewIds.filter(
                (id) => !isDefaultView(id),
              );
              await updateFolder(
                String(folderBeingEdited.id),
                name,
                customViewIds,
                views.map((v) => ({
                  id: String(v.id),
                  folderId: v.folderId ? String(v.folderId) : null,
                })),
                defaultViewIds,
              );
              setFolderBeingEdited(null);
            }}
            reservedFolderNames={reservedFolderNames}
            views={allViewsForModal}
          />
        )}
      </>
    );
  },
);

const SupportCreateFolderItem = memo(function SupportCreateFolderItem({
  onClick,
}: {
  onClick: () => void;
}) {
  const { t } = useTranslation();
  return (
    <RedoSecondaryNavItem
      action={{ type: "button", onClick }}
      friendlyName={t("+ Create folder", {
        context: "customer support navigation label",
      })}
      id="create-folder"
    />
  );
});

const FaqItems = memo(function FaqItems() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  const canManageFaq = useHasPermission(
    SettingsPermissions.SET_SUPPORT_SETTINGS,
  );

  const faqBaseUrl = routes.faqBaseUrl;
  const anyChildSelected = useMatch(`${faqBaseUrl}/*`);

  if (!canManageFaq) {
    return null;
  }

  return (
    <RedoSecondaryNavItem
      action={{ type: "button" }}
      anyChildSelected={!!anyChildSelected}
      FilledIcon={QuestionCircleIcon}
      friendlyName={t("FAQ", { context: "customer support navigation label" })}
      id="faq"
      UnfilledIcon={QuestionCircleIcon}
    >
      <FaqContentItem />
      <FaqStyleItem />
    </RedoSecondaryNavItem>
  );
});

const FaqContentItem = memo(function FaqContentItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: `${routes.faqBaseUrl}/home` }}
      friendlyName={t("Content", {
        context: "FAQ navigation page for managing help center content",
      })}
      id="faq-collections-and-articles"
    />
  );
});

const FaqStyleItem = memo(function FaqStyleItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: `${routes.faqBaseUrl}/editor/theme` }}
      friendlyName={t("Style", {
        context: "FAQ navigation page for customizing help center appearance",
      })}
      id="faq-theme-editor"
    />
  );
});

const SupportV2EditFolderItem = memo(function SupportV2EditFolderItem({
  onClick,
}: {
  onClick: () => void;
}) {
  const { t } = useTranslation();
  return (
    <RedoSecondaryNavItem
      action={{ type: "button", onClick }}
      FilledIcon={EditPencilIcon}
      friendlyName={t("Edit folder", {
        context: "customer support navigation label",
      })}
      id="edit-folder"
      UnfilledIcon={EditPencilIcon}
    />
  );
});

const SupportV2DefaultViewItem = memo(function SupportV2DefaultViewItem({
  defaultView,
  filterGroup,
  href,
  notificationCount,
  selected,
}: {
  defaultView: DefaultView;
  filterGroup: FilterGroup;
  href: string;
  notificationCount: number;
  selected: boolean;
}) {
  const defaultViewDescription = useDefaultViewDescription();
  const defaultViewDisplayName = useDefaultViewDisplayName();
  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href }}
      friendlyName={defaultViewDisplayName(defaultView)}
      id={defaultView}
      itemTooltip={viewItemTooltip(
        defaultViewDisplayName(defaultView),
        filterGroup,
        defaultViewDescription(defaultView),
      )}
      notificationCount={notificationCount}
      selected={selected}
    />
  );
});

const SupportV2ViewItem = memo(function SupportV2ViewItem({
  filterGroup,
  href,
  id,
  name,
  notificationCount,
  selected,
}: {
  filterGroup: FilterGroup;
  href: string;
  id: string;
  name: string;
  notificationCount: number;
  selected: boolean;
}) {
  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href }}
      friendlyName={name}
      id={id}
      itemTooltip={viewItemTooltip(name, filterGroup)}
      notificationCount={notificationCount}
      selected={selected}
    />
  );
});
